/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import CodeEntry from "./CodeEntry";
import StringEntry from "./StringEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import ReferenceEntry, { displayStringFromFHIRReference } from "./ReferenceEntry";
import UriEntry from "./UriEntry";
import PeriodEntry from "./PeriodEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import { Button, Dropdown, Modal, Table } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { startingIdentifierTypeOptions } from "../dictionaries/CodeSystemLookup";

const displayStringFromFHIRIdentifier = (identifier) => {
  if (identifier === undefined || identifier === null || identifier === '') {
    return ""
  }
  if (typeof identifier === "object") {
    let use = identifier.use || "";
    let type = displayStringFromFHIRCodeableConcept(identifier.type) || "";
    let system = identifier.system || "";
    let value = identifier.value || "[No identifier value.]";
    let assigner = displayStringFromFHIRReference(identifier.assigner) || "";
    if (system) {
      switch (system) {
        case 'https://clinicaltrials.gov':
          return `ClinicalTrials.gov ${value}`
        case 'https://clinicaltrials.gov/nctid':
          return `ClinicalTrials.gov NCTID ${value}`
        case 'https://euclinicaltrials.eu/clinicalTrialId':
          return `EU CTIS clinicalTrialId ${value}`
        case 'https://doi.org':
          return `DOI ${value}`
        case 'https://www.crd.york.ac.uk/prospero/':
          return `PROSPERO ${value}`
        case 'https://fevir.net/FOI':
          return `FOI ${value}`
        case 'https://fevir.net':
          if (type === "FOI" || type === "FEvIR Object Identifier") {
            return `FOI ${value}`
          } else if (type === "FLI" || type === "FEvIR Linking Identifier") {
            return `FEvIR Linking Identifier ${value}`
          } else {
            return `FEvIR Identifier ${value}`
          }
        case 'urn:ietf:rfc:3986':
          if (type === "FOI" || type === "FEvIR Object Identifier") {
            return `FOI ${value}`
          } else {
            return `Identifier ${value} from ${system}`
          }
        case 'https://fevir.net/FLI':
          return `FEvIR Linking Identifier ${value}`
        case 'https://www.ncbi.nlm.nih.gov/pmc/':
          return `PMCID ${value}`
        case 'https://www.ncbi.nlm.nih.gov/pmc':
          return `PMCID ${value}`
        case 'https://pubmed.ncbi.nlm.nih.gov':
          return `PMID ${value}`
        case 'https://pubmed.ncbi.nlm.nih.gov/':
          return `PMID ${value}`
        case 'https://orcid.org/':
          return `ORCID ${value}`
        case 'https://orcid.org':
          return `ORCID ${value}`
        default:
          return `Identifier ${value} from ${system}`;
      }
    }
    if (type) {
      let typeLower = type.toLowerCase();
      if (!typeLower.includes("identifier") || !typeLower.includes(" id")) {
        return `${type} identifier ${value}`;
      } else {
        return `${type} ${value}`;
      }
    }
    if (assigner) {
      return `identifier ${value} assigned by ${assigner}`
    }
    if (use) {
      return `${use} identifier ${value}`
    }
    return `${value}`
  }
  if (typeof identifier === 'string') {
    return `${identifier} [ERROR: Identifier data not in expected Identifier datatype.]`;
  }
  return "[ERROR: Identifier data not interpretable.]";
}

const identifierDotUseValues = ["usual", "official", "temp", "secondary", "old"];
const IdentifierEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
  startCollapsed, preserveFOI, inTableCell, dataEntryStyle, containedResourceDictionary = {} }) => {

  let isFOI = false;
  if (preserveFOI) {
    if (startingValue?.type?.text === 'FEvIR Object Identifier' || startingValue?.type?.text === 'FOI') {
      isFOI = true;
    }
  }

  const [identifierState, setIdentifierState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingIdentifier = { "use": "", "type": "", "system": "", "value": "", "period": "", "assigner": "" };
    if (startingValue) {
      if (startingValue.extension) { startingIdentifier.extension = startingValue.extension; }
      if (startingValue.use) { startingIdentifier.use = startingValue.use; }
      if (startingValue.type) { startingIdentifier.type = startingValue.type; }
      if (startingValue.system) { startingIdentifier.system = startingValue.system; }
      if (startingValue.value) { startingIdentifier.value = startingValue.value; }
      if (startingValue.period) { startingIdentifier.period = startingValue.period; }
      if (startingValue.assigner) { startingIdentifier.assigner = startingValue.assigner; }
    }
    setIdentifierState(JSON.parse(JSON.stringify(startingIdentifier)));
  }, []);

  useEffect((() => {
    if (Object.keys(identifierState).length) {
      let newIdentifier = {};
      if (identifierState.extension) { newIdentifier.extension = identifierState.extension; }
      if (identifierState.use) { newIdentifier.use = identifierState.use; }
      if (identifierState.type && Object.keys(identifierState.type).length) { newIdentifier.type = identifierState.type; }
      if (identifierState.system) { newIdentifier.system = identifierState.system; }
      if (identifierState.value) { newIdentifier.value = identifierState.value; }
      if (identifierState.period && Object.keys(identifierState.period).length) {
        newIdentifier.period = identifierState.period;
      }
      if (identifierState.assigner && Object.keys(identifierState.assigner).length) {
        newIdentifier.assigner = identifierState.assigner;
      }
      if (Object.keys(newIdentifier).length === 0) {
        newIdentifier = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newIdentifier } });
    }
  }), [identifierState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRIdentifier(startingValue)}
        &nbsp;&nbsp;
        {!isFOI && <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />}
      </p>
    </>
  } else if (Object.keys(identifierState).length > 0) {
    return <>
      {isFOI ?
        <><p>FOI {startingValue.value ? startingValue.value : '[Missing]'}</p></>
        :
        <>
          <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
          <div style={{ marginLeft: "24px" }}>
            <span>
              <b>Identifier Value: </b>
              <StringEntry elementName='value' fieldLabel='Identifier Value' startingValue={identifierState.value} setResourceState={setIdentifierState} />
            </span>
            <CodeEntry elementName='use' fieldLabel='Identifier Use' startingValue={identifierState.use} setResourceState={setIdentifierState}
              allowedValues={identifierDotUseValues} dataEntryStyle='dropdown' />
            <CodeableConceptEntry elementName='type' fieldLabel='Type of Identifier'
              startingValue={identifierState.type} setResourceState={setIdentifierState}
              dataEntryStyle={"simple"} inTableCell={inTableCell} startCollapsed={true} />
            <UriEntry elementName='system' fieldLabel='URI for Identification System'
              startingValue={identifierState.system ?
                identifierState.system : dataEntryStyle === "humanAuthor" ?
                  "https://orcid.org/" : ""}
              setResourceState={setIdentifierState} />
            <PeriodEntry elementName='period' fieldLabel='Period (when Identifier is effective)'
              inTableCell={inTableCell}
              startingValue={identifierState.period} setResourceState={setIdentifierState} startCollapsed={true} />
            <ReferenceEntry elementName='assigner' fieldLabel='Assigner of Identifier'
              containedResourceDictionary={containedResourceDictionary}
              startingValue={identifierState.assigner} setResourceState={setIdentifierState}
              referencedResourceTypes={['Organization']} startCollapsed={true} />
          </div>
        </>
      }
    </>
  }
}
//TODO - should this be a React Component with {} in the arguments?
const generateIdentifierSystemCell = (identifier, index, editMode, systemLookup, displayLookup,
  identifierTypeOptions = { options: startingIdentifierTypeOptions }, setResourceElementState, elementName) => {

  return <Table.Cell style={{ paddingRight: "0px", paddingTop: "8px", paddingBottom: "8px", verticalAlign: "top" }}>
    {editMode ?
      <>
        <Dropdown
          name="identifierdisplay"
          placeholder="Select one"
          closeOnChange selection clearable selectOnBlur={false} autoComplete='off'
          style={{ minWidth: "15%", width: "15%" }}
          options={identifierTypeOptions.options || startingIdentifierTypeOptions}
          value={systemLookup[identifier.system]?.display || ''}
          onChange={(e, data) => {
            if (data.value && displayLookup[data.value]) {
              setResourceElementState(prevState => {
                let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
                newArray[index]["system"] = displayLookup[data.value].system;
                if (displayLookup[data.value].typeText !== undefined) {
                  newArray[index]["type"] = { "text": displayLookup[data.value].typeText };
                }
                if (displayLookup[data.value].assignerDisplay !== undefined) {
                  newArray[index]["assigner"] = { "display": displayLookup[data.value].assignerDisplay };
                }
                return { ...prevState, [elementName]: newArray };
              });
            } else {
              setResourceElementState(prevState => {
                let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
                newArray[index]["system"] = "";
                newArray[index]["type"] = "";
                newArray[index]["assigner"] = "";
                return { ...prevState, [elementName]: newArray };
              });
            }
          }}
        />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "37%" }} multiline className="inputField" type='text'
          label={identifier.system ? "System URL" : "System URL"} size="small"
          value={identifier.system || ''} onChange={(e) => {
            setResourceElementState(prevState => {
              let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
              newArray[index]["system"] = e.target.value;
              return { ...prevState, [elementName]: newArray };
            });
          }}
          variant={systemLookup[identifier.system]?.systemDisabled ? 'filled' : 'outlined'}
          disabled={systemLookup[identifier.system] ? systemLookup[identifier.system].systemDisabled : false} />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "15%" }} multiline className="inputField" type='text'
          label={(identifier.type?.text !== undefined && identifier.type?.text !== "") ?
            "Name/Type" : systemLookup[identifier.system]?.typeDisabled ?
              "Name/Type N/A" : "Name/Type"}
          size="small"
          value={systemLookup[identifier.system]?.typeText !== undefined ?
            systemLookup[identifier.system].typeText : identifier.type?.text ?
              identifier.type.text : ''}
          onChange={(e) => {
            setResourceElementState(prevState => {
              let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
              newArray[index]["type"] = { "text": e.target.value };
              return { ...prevState, [elementName]: newArray };
            });
          }}
          variant={systemLookup[identifier.system]?.typeDisabled ? 'filled' : 'outlined'}
          disabled={systemLookup[identifier.system] ? systemLookup[identifier.system].typeDisabled : false} />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "26%" }} multiline className="inputField" type='text'
          label={identifier.assigner?.display ? "Assigning Organization" : "Assigning Organization"}
          size="small"
          value={systemLookup[identifier.system]?.assignerDisplay !== undefined ?
            systemLookup[identifier.system].assignerDisplay : identifier.assigner?.display ?
              identifier.assigner.display : ''}
          onChange={(e) => {
            setResourceElementState(prevState => {
              let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
              newArray[index]["assigner"] = { "display": e.target.value };
              return { ...prevState, [elementName]: newArray };
            });
          }}
          variant={systemLookup[identifier.system]?.assignerDisabled ? 'filled' : 'outlined'}
          disabled={systemLookup[identifier.system] ? systemLookup[identifier.system].assignerDisabled : false} />
      </>
      :
      <>
        {identifier.system && systemLookup[identifier.system] ?
          systemLookup[identifier.system].display : identifier.type?.text ?
            identifier.type.text : identifier.system ?
              identifier.system : identifier.assigner?.display}
      </>
    }
  </Table.Cell>
}

//TODO - should this be a React Component with {} in the arguments?
const generateIdentifierValueCell = (identifier, index, editMode, setResourceElementState, elementName) => {
  return <Table.Cell style={{ paddingRight: "0px", paddingTop: "8px", paddingBottom: "8px", verticalAlign: "top" }}>
    {editMode ?
      <TextField style={{ width: "100%" }} multiline className="inputField" type='text'
        label={"Identifier Value"} size="small" variant='outlined'
        value={identifier.value || ''}
        onChange={(e) => {
          setResourceElementState(prevState => {
            let newArray = JSON.parse(JSON.stringify(prevState[elementName]));
            newArray[index]["value"] = e.target.value;
            return { ...prevState, [elementName]: newArray };
          });
        }} />
      :
      <>
        {identifier.value}
      </>
    }
  </Table.Cell>
}

const DeleteIdentifierButton = ({ elementName, setResourceElementState, index, identifierString }) => {
  return (
    <Modal
      trigger={<span className={"unselectable"}
        style={{ cursor: "pointer", fontWeight: "normal", fontSize: "42px" }}
        title="Remove this entire identifier">🗑</span>}
      header='Are you sure you want to delete the Identifier?'
      centered={false}
      content={<div style={{ paddingLeft: "20px" }}>
        {identifierString && <span><b>Delete: </b><span style={{ whiteSpace: "pre-wrap" }}>{identifierString}</span></span>}
      </div>}
      actions={[
        {
          key: 'delete',
          positive: false,
          content: 'Delete',
          onClick: () => {
            setResourceElementState(prevState => {
              let identifierArray = [...prevState[elementName]];
              identifierArray.splice(index, 1); //deleting the item from the array
              return { ...prevState, [elementName]: identifierArray };
            });
          }
        },
        {
          key: 'done',
          content: 'Close',
          negative: true
        }
      ]}
    />
  )
}

//TODO - should this be a React Component with {} in the arguments?
const generateIdentifierTable = (identifiers, identifierClass, editMode, systemLookup, displayLookup,
  identifierTypeOptions, setResourceElementState, elementName) => {
  let buttonText = "+ Add Identifier";
  if (identifierClass === "relatedIdentifiers") {
    buttonText = "+ Add Related Identifier"
  }
  return <>
    <Table>
      <Table.Header>
        <Table.Row>
          {editMode && <Table.HeaderCell>Delete</Table.HeaderCell>}
          <Table.HeaderCell style={editMode ? { width: "75%" } : { width: "30%" }}>Identifier System</Table.HeaderCell>
          <Table.HeaderCell style={editMode ? { width: "25%" } : { width: "70%" }}>Identifier Value</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {identifiers?.map((identifier, identifierKey) => {
          return <Table.Row key={identifierKey}>
            {editMode && <Table.Cell style={{ verticalAlign: "top", align: "center" }}>
              <div style={{ width: "100%", textAlign: "center", paddingTop: "10px" }}>
                <DeleteIdentifierButton elementName={elementName}
                  setResourceElementState={setResourceElementState}
                  index={identifierKey} identifierString={displayStringFromFHIRIdentifier(identifier)} />
              </div>
            </Table.Cell>}
            {generateIdentifierSystemCell(identifier, identifierKey, editMode, systemLookup, displayLookup, identifierTypeOptions, setResourceElementState, elementName)}
            {generateIdentifierValueCell(identifier, identifierKey, editMode, setResourceElementState, elementName)}
          </Table.Row>;
        })
        }
      </Table.Body>
    </Table>
    <span>
      {editMode &&
        <Button className="formButton" style={{ color: "#000000" }}
          content={buttonText}
          onClick={() => {
            setResourceElementState(prevState => {
              let newArray = [{}];
              if (prevState[elementName]) {
                newArray = prevState[elementName].concat([{}]);
              }
              return { ...prevState, [elementName]: newArray };
            });
          }} />}
    </span>
  </>
}

export default IdentifierEntry;
export { displayStringFromFHIRIdentifier, generateIdentifierTable };